import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons';
import { t } from 'i18next';
import toast from 'react-hot-toast';
import { useSelector, AppState } from 'src/store/Store';
import useLang from 'src/hooks/useLang';
import { useCreateChatRoomMutation } from 'src/services/chat';
import { ensureChatSignIn, readRoomStadiumId } from 'src/hooks/useFirebaseMessages';
import useChatDetails from 'src/hooks/useChatDetails';
import useAnalytics, { AnalyticsEvent } from 'src/hooks/useAnalytics';
import { ChatTarget } from 'src/types/apps/messages';
import {
  getInitials,
  getAvatarColor,
  formatDayLabel,
  formatHeaderMessageTime,
} from 'src/utils/chatAvatar';
import MessageBubble from './MessageBubble';
import DayDivider from './DayDivider';
import ChatComposer from './ChatComposer';

interface ChatConversationProps {
  target: ChatTarget;
  onBack: () => void;
  onFailedOpen?: () => void;

  /** Called once the room id is known — immediately for an existing room,
   * or after creation for a brand-new one (Match Players entry, no chatId
   * yet). Lets callers that track chatId outside this component (e.g. the
   * Chats page's stadium-switch guard) stay in sync so a newly-created
   * conversation isn't left open — and stamped with the wrong stadium —
   * after the user switches stadiums (Bugbot: wrong stadium on send). */
  onResolved?: (chatId: string, stadiumId?: number) => void;

  /** Show the back control — used on mobile stacked layout. Default true. */
  showBack?: boolean;
}

const isPhoneSubtitle = (value?: string) =>
  !!value && /^[+(0-9\u0660-\u0669\u06F0-\u06F9]/.test(value.trim());

// In-page conversation view used by the Chats screen. Resolves/creates the
// room, subscribes to messages, and clears unread on open.
const ChatConversation = ({
  target,
  onBack,
  onFailedOpen,
  onResolved,
  showBack = true,
}: ChatConversationProps) => {
  const currentUserId = useSelector((state: AppState) => state.authReducer.user?.data?.id);
  const { isRTL } = useLang();
  const { trackEvent } = useAnalytics();

  const [createChatRoom] = useCreateChatRoomMutation();
  const [resolvedChatId, setResolvedChatId] = useState<string | null>(null);
  const [roomStadiumId, setRoomStadiumId] = useState<number | undefined>(target.stadiumId);
  const [isResolving, setIsResolving] = useState(false);
  const deferRoomCreation = target.deferRoomCreation === true && !target.chatId;

  const {
    messages,
    loading,
    loadingEarlier,
    hasMoreEarlier,
    participantCount,
    isUploading,
    uploadProgress,
    loadEarlier,
    sendMessage,
    sendImageMessage,
  } = useChatDetails(resolvedChatId, roomStadiumId);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const isLoadingEarlierRef = useRef(false);
  const resolvedChatIdRef = useRef<string | null>(null);
  resolvedChatIdRef.current = resolvedChatId;
  const resolvedStadiumIdRef = useRef<number | undefined>(roomStadiumId);
  resolvedStadiumIdRef.current = roomStadiumId;

  // Returns the authoritative {chatId, stadiumId} directly, instead of
  // callers reading `roomStadiumId` state — that state may not have
  // re-rendered yet by the time a send immediately follows resolution
  // (deferred room creation), which let sendMessage's stadium-mismatch
  // guard check a stale value and silently deliver into a room the UI
  // then closes (Bugbot: sent message vanishes on stadium mismatch).
  const ensureRoomCreated = useCallback(async (): Promise<{
    chatId: string;
    stadiumId?: number;
  }> => {
    if (resolvedChatIdRef.current) {
      return { chatId: resolvedChatIdRef.current, stadiumId: resolvedStadiumIdRef.current };
    }
    if (target.chatId) {
      resolvedChatIdRef.current = target.chatId;
      resolvedStadiumIdRef.current = target.stadiumId;
      setResolvedChatId(target.chatId);
      onResolved?.(target.chatId, target.stadiumId);
      return { chatId: target.chatId, stadiumId: target.stadiumId };
    }

    try {
      await ensureChatSignIn(currentUserId);
      const result = await createChatRoom({
        type: target.chatType,
        target_id: [target.targetId],
        stadium_id: target.stadiumId,
      }).unwrap();

      let stadiumId = target.stadiumId;
      if (currentUserId != null) {
        const fromRoom = await readRoomStadiumId(result.chat_id, currentUserId);
        if (fromRoom != null) stadiumId = fromRoom;
      }

      resolvedChatIdRef.current = result.chat_id;
      resolvedStadiumIdRef.current = stadiumId;
      if (stadiumId != null) setRoomStadiumId(stadiumId);
      setResolvedChatId(result.chat_id);
      onResolved?.(result.chat_id, stadiumId);

      return { chatId: result.chat_id, stadiumId };
    } catch {
      toast.error(t('Chat.failedToOpen'));
      throw new Error('Chat room creation failed');
    }
  }, [
    createChatRoom,
    currentUserId,
    onResolved,
    target.chatId,
    target.chatType,
    target.stadiumId,
    target.targetId,
  ]);

  useEffect(() => {
    // Persisting chatId onto the Redux target (so remounts don't restore a
    // deferred empty draft) must not tear down an in-flight conversation.
    if (target.chatId && resolvedChatIdRef.current === target.chatId) {
      return;
    }

    let cancelled = false;
    setIsResolving(true);
    setResolvedChatId(null);
    setRoomStadiumId(target.stadiumId);
    lastMessageIdRef.current = null;
    resolvedChatIdRef.current = null;

    (async () => {
      try {
        await ensureChatSignIn(currentUserId);
        if (cancelled) return;

        const resolveStadium = async (chatId: string): Promise<number | undefined> => {
          // Existing private rooms are reused across stadiums. Match Players
          // stamps the live stadium, which is wrong when createChatRoom
          // returns private_{uidA}_{uidB} from an earlier stadium. The room
          // record wins; the entry-point value is only a fallback for a
          // brand-new room that has not been written yet.
          if (currentUserId != null) {
            const fromRoom = await readRoomStadiumId(chatId, currentUserId);
            if (fromRoom != null) return fromRoom;
          }

          return target.stadiumId;
        };

        if (target.chatId) {
          const stadiumId = await resolveStadium(target.chatId);
          if (cancelled) return;
          if (stadiumId != null) setRoomStadiumId(stadiumId);
          resolvedChatIdRef.current = target.chatId;
          setResolvedChatId(target.chatId);
          onResolved?.(target.chatId, stadiumId);

          return;
        }

        if (deferRoomCreation) {
          return;
        }

        const result = await createChatRoom({
          type: target.chatType,
          target_id: [target.targetId],
          stadium_id: target.stadiumId,
        }).unwrap();
        if (!cancelled) {
          const stadiumId = await resolveStadium(result.chat_id);
          if (cancelled) return;
          if (stadiumId != null) setRoomStadiumId(stadiumId);
          resolvedChatIdRef.current = result.chat_id;
          setResolvedChatId(result.chat_id);
          onResolved?.(result.chat_id, stadiumId);
        }
      } catch {
        if (!cancelled) {
          toast.error(t('Chat.failedToOpen'));
          onFailedOpen?.();
          onBack();
        }
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.chatId, target.chatType, target.targetId, currentUserId, deferRoomCreation]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || messages.length === 0) return;

    const newestId = messages[messages.length - 1]?.id ?? null;

    // Skip auto-scroll when prepending history via "Load earlier".
    if (isLoadingEarlierRef.current) {
      isLoadingEarlierRef.current = false;
      lastMessageIdRef.current = newestId;
      return;
    }

    if (newestId && newestId !== lastMessageIdRef.current) {
      el.scrollTop = el.scrollHeight;
      lastMessageIdRef.current = newestId;
    }
  }, [messages]);

  const isGroup = target.chatType === 'match';
  const isSharedOwnerChat = target.chatType === 'stadium' || target.chatType === 'stadium_private';

  const lastMessageAt = messages[messages.length - 1]?.createdAt;
  const lastMessageTime = lastMessageAt ? formatHeaderMessageTime(lastMessageAt) : '';
  const isBusy = isResolving || (loading && !!resolvedChatId);
  const composerDisabled = isBusy || isUploading || (!resolvedChatId && !deferRoomCreation);

  const handleLoadEarlier = async () => {
    if (loadingEarlier) return;
    isLoadingEarlierRef.current = true;
    const el = scrollContainerRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    await loadEarlier();
    // Preserve viewport after prepend.
    requestAnimationFrame(() => {
      if (!el) return;
      el.scrollTop = el.scrollHeight - prevHeight + prevTop;
    });
  };

  const handleSendMessage = async (text: string) => {
    try {
      const resolved = deferRoomCreation
        ? await ensureRoomCreated()
        : { chatId: resolvedChatId, stadiumId: roomStadiumId };
      if (!resolved.chatId) return;
      await sendMessage(text, resolved.chatId, resolved.stadiumId);
      trackEvent(AnalyticsEvent.SEND_CHAT_MESSAGE, { chat_type: target.chatType, type: 'text' });
    } catch {
      // ensureRoomCreated / sendMessage already toast
    }
  };

  const handleSendImage = async (file: File, caption?: string) => {
    try {
      const resolved = deferRoomCreation
        ? await ensureRoomCreated()
        : { chatId: resolvedChatId, stadiumId: roomStadiumId };
      if (!resolved.chatId) return;
      await sendImageMessage(file, caption, resolved.chatId, resolved.stadiumId);
      trackEvent(AnalyticsEvent.SEND_CHAT_MESSAGE, { chat_type: target.chatType, type: 'image' });
    } catch {
      // ensureRoomCreated / sendImageMessage already toast
    }
  };

  const groupedMessages = messages.reduce<
    { dayKey: string; label: string; items: typeof messages }[]
  >((groups, message) => {
    const dayKey = new Date(message.createdAt).toDateString();
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.dayKey === dayKey) {
      lastGroup.items.push(message);
    } else {
      groups.push({ dayKey, label: formatDayLabel(message.createdAt), items: [message] });
    }
    return groups;
  }, []);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '18px 26px',
          borderBottom: '1px solid #EEF2F6',
          flexShrink: 0,
        }}
      >
        {showBack && (
          <>
            <Box
              onClick={onBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#5A6472',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {isRTL ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
              {t('Common.back')}
            </Box>
            <Box sx={{ width: '1px', height: 26, backgroundColor: '#EEF2F6', flexShrink: 0 }} />
          </>
        )}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '15px',
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
            backgroundColor: getAvatarColor(resolvedChatId || target.title, isGroup),
          }}
        >
          {getInitials(target.title)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }} noWrap>
            {target.title}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#8B95A1' }} noWrap>
            {isGroup ? (
              t('Chat.participants', { count: participantCount })
            ) : isPhoneSubtitle(target.subtitle) ? (
              <Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate' }}>
                {target.subtitle}
              </Box>
            ) : (
              target.subtitle
            )}
            {(isGroup || target.subtitle) && lastMessageTime ? ' • ' : null}
            {lastMessageTime}
          </Typography>
        </Box>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          backgroundColor: '#FAFBFC',
          padding: '26px 34px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        {isBusy ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '13.5px', color: '#9AA3AD' }}>
              {deferRoomCreation ? t('Chat.startChatEmptyHint') : t('Chat.noMessagesYet')}
            </Typography>
          </Box>
        ) : (
          <>
            {hasMoreEarlier && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  onClick={() => !loadingEarlier && handleLoadEarlier()}
                  sx={{
                    fontSize: '12px',
                    color: '#6FA323',
                    fontWeight: 600,
                    cursor: loadingEarlier ? 'default' : 'pointer',
                  }}
                >
                  {loadingEarlier ? <CircularProgress size={14} /> : t('Chat.loadEarlier')}
                </Typography>
              </Box>
            )}
            {groupedMessages.map((group) => (
              <Box
                key={group.dayKey}
                sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <DayDivider label={group.label} />
                {group.items.map((message) => {
                  // Stadium-side messages (any PO on this dashboard) render on
                  // the right — not keyed to the logged-in staff member's id.
                  const isOwn = message.senderType === 'stadium';
                  const senderLabel = isOwn ? message.senderName : message.user.name;

                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      isOwn={isOwn}
                      showSenderName={
                        !!senderLabel && ((isGroup && !isOwn) || (isSharedOwnerChat && isOwn))
                      }
                      senderLabel={senderLabel}
                    />
                  );
                })}
              </Box>
            ))}
          </>
        )}
      </Box>

      <ChatComposer
        onSend={handleSendMessage}
        onSendImage={handleSendImage}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        disabled={composerDisabled}
      />
    </Box>
  );
};

export default ChatConversation;
